<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import coachService from '../services/coach.service'
import categoryService from '../services/category.service'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const firstName = ref('')
const lastName = ref('')
const selectedCategories = ref<any[]>([])
const availableCategories = ref<any[]>([])
const email = ref('')
const phone = ref('')
const birthDate = ref('')
const licenseNumber = ref('')
const photo = ref<File | null>(null)
const currentPhotoUrl = ref<string | null>(null)
const loading = ref(false)
const loadingData = ref(false)
const loadingCategories = ref(false)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

// Get club UUID and coach UUID
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)
const coachUuid = computed(() => route.params.uuid as string)

// Fetch coach data
const fetchCoach = async () => {
  if (!clubUuid.value || !coachUuid.value) {
    error.value = t('coaches.errorNoClubOrCoach')
    return
  }

  loadingData.value = true

  try {
    const response = await coachService.getCoach(clubUuid.value, coachUuid.value)
    const coach = response.coach

    // Populate form fields
    firstName.value = coach.first_name || ''
    lastName.value = coach.last_name || ''
    email.value = coach.email || ''
    phone.value = coach.phone || ''
    birthDate.value = coach.birth_date || ''
    licenseNumber.value = coach.coach_license_number || ''
    currentPhotoUrl.value = coach.photo_path || null

    // Set selected categories once available categories are loaded
    if (coach.categories && availableCategories.value.length > 0) {
      selectedCategories.value = availableCategories.value.filter(cat =>
        coach.categories.some((c: any) => c.uuid === cat.uuid)
      )
    }
  } catch (err: any) {
    console.error('Error fetching coach:', err)
    error.value = err.response?.data?.message || t('coaches.errorFetchingCoach')

    if (err.response?.status === 404) {
      toastStore.error(t('coaches.coachNotFound'))
      router.push('/coaches')
    }
  } finally {
    loadingData.value = false
  }
}

// Fetch available categories
const fetchCategories = async () => {
  if (!clubUuid.value) {
    error.value = t('coaches.errorNoClub')
    return
  }

  loadingCategories.value = true

  try {
    const response = await categoryService.getCategories(clubUuid.value, 1, 9999)
    availableCategories.value = response.data || []
  } catch (err: any) {
    console.error('Error fetching categories:', err)
    error.value = err.response?.data?.message || t('coaches.errorFetchingCategories')
  } finally {
    loadingCategories.value = false
  }
}

// Crop image to square
const cropImageToSquare = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        // Calculate square size (minimum of width/height)
        const size = Math.min(img.width, img.height)
        canvas.width = size
        canvas.height = size

        // Calculate crop position (center crop)
        const offsetX = (img.width - size) / 2
        const offsetY = (img.height - size) / 2

        // Draw cropped image
        ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size)

        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            const croppedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            })
            resolve(croppedFile)
          } else {
            reject(new Error('Could not create blob'))
          }
        }, file.type)
      }
      img.onerror = reject
      img.src = e.target?.result as string
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// Handle photo upload
const handlePhotoChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    const file = target.files[0]

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      error.value = t('coaches.selectImageFile')
      return
    }

    try {
      // Crop image to square
      photo.value = await cropImageToSquare(file)
    } catch (err) {
      console.error('Error cropping image:', err)
      error.value = t('coaches.errorProcessingImage')
    }
  }
}

// Handle form submission
const handleSubmit = async () => {
  if (!clubUuid.value || !coachUuid.value) {
    error.value = t('coaches.errorNoClubOrCoach')
    return
  }

  loading.value = true
  error.value = null
  validationErrors.value = {}

  try {
    const formData = new FormData()
    formData.append('first_name', firstName.value.trim())
    formData.append('last_name', lastName.value.trim())

    if (email.value.trim()) {
      formData.append('email', email.value.trim())
    }

    if (phone.value.trim()) {
      formData.append('phone', phone.value.trim())
    }

    if (birthDate.value) {
      formData.append('birth_date', birthDate.value)
    }

    if (licenseNumber.value.trim()) {
      formData.append('coach_license_number', licenseNumber.value.trim())
    }

    if (photo.value) {
      formData.append('photo', photo.value)
    }

    // Add category UUIDs
    selectedCategories.value.forEach(category => {
      formData.append('category_uuids[]', category.uuid)
    })

    const response = await coachService.updateCoach(clubUuid.value, coachUuid.value, formData)

    // Show success toast
    const message = response?.message || t('coaches.coachUpdated')
    toastStore.success(message)

    // Redirect to coach detail
    router.push(`/coaches/${coachUuid.value}`)
  } catch (err: any) {
    console.error('Update coach error:', err)

    // Handle validation errors
    if (err.response?.data?.errors) {
      validationErrors.value = err.response.data.errors
    } else {
      // Show error as toast instead of replacing the form
      const errorMessage = err.response?.data?.message || t('coaches.errorUpdatingCoach')
      toastStore.error(errorMessage)
    }
  } finally {
    loading.value = false
  }
}

// Get validation error for field
const getFieldError = (fieldName: string): string | null => {
  const errors = validationErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : null
}

// Check if field has error
const hasFieldError = (fieldName: string): boolean => {
  return !!validationErrors.value[fieldName]
}

// Handle cancel
const handleCancel = () => {
  router.push(`/coaches/${coachUuid.value}`)
}

// Custom label for multiselect
const categoryLabel = ({ name }: any) => name

onMounted(async () => {
  await fetchCategories()
  await fetchCoach()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link :to="`/coaches/${coachUuid}`" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('coaches.backToDetail') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('coaches.editCoach') }}</h1>
      </div>

      <!-- Loading State -->
      <div v-if="loadingData" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('coaches.loadingCoach') }}</p>
      </div>

      <!-- Error Message -->
      <div v-else-if="error && !loadingData" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Form -->
      <div v-else class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSubmit">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- First Name -->
            <div>
              <label for="firstName" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('coaches.firstName') }} <span class="text-red-600">{{ t('common.required') }}</span>
              </label>
              <input
                type="text"
                id="firstName"
                v-model="firstName"
                required
                :class="[
                  'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none',
                  hasFieldError('first_name') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
                ]"
              >
              <p v-if="hasFieldError('first_name')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('first_name') }}
              </p>
            </div>

            <!-- Last Name -->
            <div>
              <label for="lastName" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('coaches.lastName') }} <span class="text-red-600">{{ t('common.required') }}</span>
              </label>
              <input
                type="text"
                id="lastName"
                v-model="lastName"
                required
                :class="[
                  'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none',
                  hasFieldError('last_name') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
                ]"
              >
              <p v-if="hasFieldError('last_name')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('last_name') }}
              </p>
            </div>

            <!-- Categories -->
            <div>
              <label for="categories" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('coaches.categories') }}
              </label>
              <Multiselect
                v-model="selectedCategories"
                :options="availableCategories"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :placeholder="t('coaches.selectCategories')"
                label="name"
                track-by="uuid"
                :custom-label="categoryLabel"
                :loading="loadingCategories"
                :disabled="loadingCategories"
              >
                <template #noResult>{{ t('coaches.noCategoriesFound') }}</template>
                <template #noOptions>{{ t('coaches.noCategoriesAvailable') }}</template>
              </Multiselect>
              <p v-if="hasFieldError('category_uuids')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('category_uuids') }}
              </p>
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('coaches.email') }}
              </label>
              <input
                type="email"
                id="email"
                v-model="email"
                :class="[
                  'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none',
                  hasFieldError('email') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
                ]"
              >
              <div class="flex items-start gap-2 mt-2">
                <svg class="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                <span class="text-xs text-gray-600">{{ t('coaches.emailInfoMessage') }}</span>
              </div>
              <p v-if="hasFieldError('email')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('email') }}
              </p>
            </div>

            <!-- Phone -->
            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('coaches.phone') }}
              </label>
              <input
                type="tel"
                id="phone"
                v-model="phone"
                placeholder="+48 500 000 000"
                :class="[
                  'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none',
                  hasFieldError('phone') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
                ]"
              >
              <p v-if="hasFieldError('phone')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('phone') }}
              </p>
            </div>

            <!-- Birth Date -->
            <div>
              <label for="birthDate" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('coaches.birthDate') }}
              </label>
              <input
                type="date"
                id="birthDate"
                v-model="birthDate"
                :class="[
                  'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none',
                  hasFieldError('birth_date') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
                ]"
              >
              <p v-if="hasFieldError('birth_date')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('birth_date') }}
              </p>
            </div>

            <!-- License Number -->
            <div>
              <label for="licenseNumber" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('coaches.licenseNumber') }}
              </label>
              <input
                type="text"
                id="licenseNumber"
                v-model="licenseNumber"
                :class="[
                  'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none',
                  hasFieldError('coach_license_number') ? 'border-red-300 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
                ]"
              >
              <p v-if="hasFieldError('coach_license_number')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('coach_license_number') }}
              </p>
            </div>

            <!-- Profile Photo -->
            <div>
              <label for="photo" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('coaches.profilePhoto') }}
              </label>
              <input
                type="file"
                id="photo"
                accept="image/*"
                @change="handlePhotoChange"
                class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              >
              <div v-if="currentPhotoUrl" class="mt-2">
                <img :src="currentPhotoUrl" :alt="t('coaches.currentPhoto')" class="w-20 h-20 rounded object-cover">
              </div>
              <p v-if="hasFieldError('photo')" class="mt-1 text-xs text-red-600">
                {{ getFieldError('photo') }}
              </p>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end gap-2 mt-6">
            <button
              type="button"
              @click="handleCancel"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
              :disabled="loading"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="loading"
            >
              {{ loading ? t('coaches.saving') : t('coaches.saveChanges') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
